import React, { useRef } from "react";
import { styled } from '@mui/material/styles';
import { Button, Tooltip, Typography, Box } from "@mui/material";
import { CloudUpload, InsertDriveFile } from '@mui/icons-material';
import { FileItem } from "../../../constants";

const UploadContainer = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: '8px',
  padding: '12px 8px',
  textAlign: 'center',
  backgroundColor: theme.palette.grey[50],
  transition: 'all 0.2s ease',
  position: 'relative',
  minHeight: '48px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#E39435',
    backgroundColor: '#E394351A', // Orange with 10% opacity
  },
  '&.drag-over': {
    borderColor: '#E39435',
    backgroundColor: '#E3943533', // Orange with 20% opacity
  },
}));

const FilePreview = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '8px',
}));

const FileInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
});

const FileName = styled(Typography)({
  marginLeft: '8px',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
});

const HiddenInput = styled('input')({
  display: 'none',
});

const UploadIcon = styled(CloudUpload)(({ theme }) => ({
  fontSize: '1.75rem',
  color: '#E39435',
  marginBottom: '4px',
}));

const UploadText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
  marginBottom: '2px',
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  marginTop: '4px',
}));

interface ProfileFileUploadProps {
  inputId: string;
  label: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
  maxSizeMB?: number;
  files: FileItem[];
  isLoading?: boolean;
  onDelete?: () => void;
  required?: boolean;
  error?: string;
  status?: string;
}

const ProfileFileUpload: React.FC<ProfileFileUploadProps> = ({
  inputId,
  label,
  onChange,
  accept = ".pdf,.jpg,.jpeg,.png",
  maxSizeMB = 5,
  files,
  isLoading = false,
  onDelete,
  required = false,
  error,
  status
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0 && fileInputRef.current) {
      const file = droppedFiles[0];
      // Create a file list and assign it to the input element
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInputRef.current.files = dt.files;
      
      // Trigger the onChange event
      const event = new Event('change', { bubbles: true });
      fileInputRef.current.dispatchEvent(event);
    }
  };

  const getFileName = (file: FileItem): string => {
    if (file.imagePath instanceof File) {
      return file.imagePath.name;
    }
    if (file.imagePath && typeof file.imagePath === 'string') {
      const pathParts = (file.imagePath as string).split('/');
      return pathParts[pathParts.length - 1] || 'Unknown file';
    }
    return 'Unknown file';
  };

  const hasFile = files && files.length > 0;

  return (
    <Box>
      <HiddenInput
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept={accept}
        onChange={onChange}
        disabled={isLoading}
      />
      
      {!hasFile ? (
        <UploadContainer
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          sx={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
        >
          <UploadIcon />
          <UploadText>
            Drag and drop your file here or click to browse
          </UploadText>
          <Typography variant="caption" color="text.secondary">
            Supported formats: PDF, JPG, PNG (Max {maxSizeMB}MB)
          </Typography>
        </UploadContainer>
      ) : (
        <FilePreview>
          <FileInfo>
            <InsertDriveFile sx={{ color: '#E39435' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FileName>{getFileName(files[0])}</FileName>
            </Box>
          </FileInfo>
         
          <Box sx={{ display: 'flex', gap: 1 }}>
             {
            status !== 'approved' && 
            <Tooltip title="Edit file" arrow placement="top">
              <Button
                size="small"
                variant="outlined"
                onClick={handleClick}
                disabled={isLoading}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  borderColor: '#E39435',
                  color: '#E39435',
                  '&:hover': {
                    borderColor: '#D17F2E',
                    backgroundColor: '#E394351A',
                  },
                }}
              >
                Edit
              </Button>
            </Tooltip>
}
          </Box>
          
        </FilePreview>
      )}
      
      {error && <ErrorText>{error}</ErrorText>}
    </Box>
  );
};

export default ProfileFileUpload;